import React, { useEffect, useState, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import svg from '../assets/smart_welcome_icon.svg';
import Toggle from '@veneer/core/dist/scripts/toggle';
import LogoHp from '@veneer/core/dist/scripts/logos/hp/hp';
import easyStartproIcon from '../assets/Sehawk_Logo_512.png';
import { propOr } from 'ramda';
import { HPX_SUPPORTED_LANGUAGES_KEY, RTL_LANG } from '../constants/i18n';
import { determineLocale } from '../utils/determineLocale';
import { ToggleLabelHeader } from '../styles/privacySettingsStyles';
import { useHistory, useParams } from 'react-router';
import { Button, DeviceListPage, EmptyCard, EmptyPage, Loader } from '@clientos/ui-toolkit';

import {
  ReTargetContainer,
  ReTargetContentContainer,
  ReTargetBody,
  ReTargetUserConsentsBody,
  ReTargetButtonContainer,
  StyledButton,
  Container,
  LogoContainer,
  SureSupplyContainer,
  BodySureSupply,
  Graphic,
  Title,
  Body,
  ToggleLayout,
  BodyList,
  RebrandBackGround,
  RebrandCardContent,
  RebrandUserConsentsCardContent,
  ReTargetUserConsentsButtonContainer,
  RebrandUserConsentsTitle,
  RebrandEmptyPageWrapper,
  RebrandEmptyPageContentWrapper,
  ReTargetUserConsentsButtonLargeContainer,
  ReTargetPrinterButtonLargeContainer,
  ButtonLoaderWrapper,
  RebrandEmptyCard
} from '../styles/welcomeRebrandStyles';
import { requiredBasicPrinter } from '../constants/purposeID';
import { renderTrackEvent } from '../lib/analyticsTracking';
import { sendSimpleUiEvent } from '../jweb/plugins/dataCollection';
import { CreateAnalyticsControlEvent, CreateAnalyticsScreenEvent, ProcessAuxParams  } from '../utils/simpleUIAnalytics';
import useIsSmallScreen from '../utils/useIsSmallScreen';
import { ACTION_AUX_PARAMS, ACTIONS, CONTROL_NAMES, HYPERLINKS_HREFS, HYPERLINKS_URLS_ENDING_PATHS, SCREEN_NAMES, SCREEN_PATHS } from '../jweb/constants';

const SmartWelcomeRebrand = ({
  handleClick,
  declineAllClick,
  csData = {},
  alpacaData,
  loading,
  declineAllLoading,
  smallButton,
  reTarget,
  query,
  sureSupplyAnalyticsContent = '',
  sureSupplyAnalyticsConsentToggle = false,
  setSureSupplyAnalyticsConsentToggle,
  requiredWelcomeContent = '',
  errorResponse
}) => {
  const errorCode = propOr(0, 'code', errorResponse);
  const errorMessage = propOr('', 'message', errorResponse);
  const title = propOr('', 'title', csData);
  const acceptAllButton = propOr('', 'acceptAllButton', csData);
  const manageOptionsButton = propOr('', 'manageOptionButton', csData);
  const declineAllButton = propOr('', 'declineAllButton', csData);
  const continueButton = propOr('', 'button', csData);
  const continueButtonText = propOr('', 'continueButtonText', csData);
  const body = propOr('', 'body', csData);
  const secondaryBodyText = propOr('', 'secondaryBodyText', csData);
  const supplementalText = propOr('', 'supplementalText', csData);
  const buttonTitle = propOr('', 'buttonTitle', csData);
  const errorText = propOr('', 'errorText', csData);
  const printerImage = propOr('', 'printerImage', csData);
  const history = useHistory();
  const params = useParams();
  const { country, language } = determineLocale(HPX_SUPPORTED_LANGUAGES_KEY);
  const { pathname, search } = history.location;
  const { platform, version } = params;
  const [isDesktop, setDesktop] = useState(false);
  const [consentsLocale, setConsentLocaleProps] = useState(null);
  const [isRestOfWorld, setIsRestOfWorld] = useState(null);
  const isRTL = RTL_LANG.includes(language) ? true : false;
  const queryParam = query.appName || '';
  const hasAppNameSureSupply = queryParam.toLowerCase() === 'suresupply';
  const hasAppNameEasyStart = queryParam.toLowerCase() === 'easystart';
  const hasAppNameEasyStartPro = queryParam.toLowerCase() === 'easystartpro';
  const hasAppNameWJA = queryParam.toLowerCase() === 'wja';
  const isWorkFromHomePrinter = query && query.wfh && query.wfh === 'true';
  const userConsents = pathname.includes('user-consents');
  const printerConsents = pathname.includes('printer-consents');
  const isCompanyTenantPrinter = query && query.companyTenantId && query.companyTenantId !== '';
  const [isRenderAcceptAllButton, setRenderAcceptAllButton] = useState(false);
  const { checkBasicFuncPrinter, ffAnalyticsTracking } = useFlags();
  const isNarrowScreen = useIsSmallScreen(1022);
  const userButtonContainerRef = useRef();

  const printerActionAuxParams = {
    source: ACTION_AUX_PARAMS.SOURCE.HP_SMART_APP,
    flow: isWorkFromHomePrinter ? ACTION_AUX_PARAMS.FLOW.WFH : ACTION_AUX_PARAMS.FLOW.DEVICE_ONBOARDING,
  }

  const printerProcessedActionAuxParams = ProcessAuxParams(printerActionAuxParams);
  
  const defaultTrackEventParams = {
    screenPath: SCREEN_PATHS.REACT_PRIVACY_CONSENTS,
    screenName: userConsents ? SCREEN_NAMES.APP_WELCOME : SCREEN_NAMES.PRINTER_WELCOME,
    isDeclineAllButtonShown:  !isWorkFromHomePrinter && !isCompanyTenantPrinter,
    ...(printerConsents ? {actionAuxParams: printerProcessedActionAuxParams} : {}),
  }

  const consentScreenTrackEvent = CreateAnalyticsScreenEvent({
    ...defaultTrackEventParams,
    action: ACTIONS.SCREEN_DISPLAYED
  });

  const acceptAllButtonDisplayTrackEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    controlName: CONTROL_NAMES.ACCEPT_ALL,
    action: ACTIONS.CONTROL_DISPLAYED,
  })

  const acceptAllButtonClickTrackEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    controlName: CONTROL_NAMES.ACCEPT_ALL,
    action: ACTIONS.CONTROL_BUTTON_CLICKED,
  })

  const declineAllButtonClickTrackEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    controlName: CONTROL_NAMES.DECLINE_ALL,
    action: ACTIONS.CONTROL_BUTTON_CLICKED,
  })

  const manageOptionsButtonClickTrackEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    controlName: CONTROL_NAMES.MANAGE_OPTIONS,
    action: ACTIONS.CONTROL_BUTTON_CLICKED,
  })

  const hpPrivacyStatementHyperLinkClickEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    controlName: CONTROL_NAMES.HP_PRIVACY_STATEMENT,
    action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  })

  const hpTermsOfUseHyperLinkClickEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    controlName: CONTROL_NAMES.TERMS_OF_USE,
    action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  })

  const hpAITermsOfUseHyperLinkClickEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    controlName: CONTROL_NAMES.AI_TERMS_OF_USE,
    action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  })

  const eulaHyperLinkClickEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    controlName: CONTROL_NAMES.EULA,
    action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  })

  useEffect(() => {
    setDesktop(platform === 'ios' || platform === 'android' ? false : true);
    setConsentLocaleProps(determineLocale(HPX_SUPPORTED_LANGUAGES_KEY));
    sendSimpleUiEvent(consentScreenTrackEvent)
  }, [platform]);

  useEffect(() => {
    setIsRestOfWorld(consentsLocale?.country !== 'us' ? true : false);
  }, [consentsLocale]);

  useEffect(() => {
    if (alpacaData !== null && !isRenderAcceptAllButton && userConsents) {
      renderTrackEvent(ffAnalyticsTracking);
    }
    if (alpacaData !== null && !isRenderAcceptAllButton){
      setRenderAcceptAllButton(true);
    }
    if (alpacaData){
      const hyperlinkElements = document.querySelectorAll('#hpsmart-content a')
      if (hyperlinkElements) {
        hyperlinkElements.forEach((link) => {
          if (link.href === HYPERLINKS_HREFS.PRIVACY || link.href.split('/').at(-1) === HYPERLINKS_URLS_ENDING_PATHS.PRIVACY) {
            hpPrivacyStatementHyperLinkClickEvent.actionAuxParams = ProcessAuxParams({url: link.href})
            link.addEventListener('click', () => {
              sendSimpleUiEvent(hpPrivacyStatementHyperLinkClickEvent)
            });
          } else if (link.href === HYPERLINKS_HREFS.TERMS_OF_USE) {
            hpTermsOfUseHyperLinkClickEvent.actionAuxParams = ProcessAuxParams({url: link.href})
            link.addEventListener('click', () => {
              sendSimpleUiEvent(hpTermsOfUseHyperLinkClickEvent)
            });
          } else if (link.href === HYPERLINKS_HREFS.AI_TERMS_OF_USE) {
            hpAITermsOfUseHyperLinkClickEvent.actionAuxParams = ProcessAuxParams({url: link.href})
            link.addEventListener('click', () => {
              sendSimpleUiEvent(hpAITermsOfUseHyperLinkClickEvent)
            });
          }
           else if (link.href === HYPERLINKS_HREFS.EULA || link.href.split('/').at(-1) === HYPERLINKS_URLS_ENDING_PATHS.EULA || link.href.split('/').at(-1) === HYPERLINKS_URLS_ENDING_PATHS.US_EULA) {
            eulaHyperLinkClickEvent.actionAuxParams = ProcessAuxParams({url: link.href})
            link.addEventListener('click', () => {
              sendSimpleUiEvent(eulaHyperLinkClickEvent)
            });
          }
        })
      }
    }
  }, [alpacaData]);

  useEffect(() => {
    if (isRenderAcceptAllButton){
      sendSimpleUiEvent(acceptAllButtonDisplayTrackEvent);
    }
    
    if (isNarrowScreen && userButtonContainerRef.current) {
      const containerHeight = userButtonContainerRef.current.clientHeight;
      if (containerHeight > 0) {
        const pageContent = document.querySelector('.clientos-ui-toolkit-empty-page .clientos-ui-toolkit-empty-page__content')
        if (pageContent) {
          pageContent.style.paddingBottom = `${containerHeight + 16}px`;
        }
      }
    }
  }, [isRenderAcceptAllButton]);

  const redirectTo = () => {
    pathname.includes('user-consents')
      ? history.push(`/in-app/${platform}/${version}/user-consents-update${search}&onBoard=true`)
      : history.push(
          `/in-app/${platform}/${version}/printer-consents-update${search}&onBoard=true`
        );
  };

  function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);

  return matches;
}
  const isLargeScreen = useMediaQuery("(min-width: 1024px)");
  const isPrinterLargeScreen = useMediaQuery('(min-width: 768px)');
  const isSupportDeclineAllButton = () => !isWorkFromHomePrinter && !isCompanyTenantPrinter;

  const getButtonContainer = () => {
    if (reTarget) {
      if (userConsents & !isWorkFromHomePrinter) {
        if (isLargeScreen) {
          return (
            <ReTargetUserConsentsButtonLargeContainer
              data-testid="redirect-container"
              isDesktop={isDesktop}
              isRestOfWorld={isRestOfWorld}
              isRTL={isRTL}
              id="re-target-button-container"
              ref={userButtonContainerRef}
            >
              {!isWorkFromHomePrinter && !isCompanyTenantPrinter && !hasAppNameWJA && (
                <Button
                  data-testid="redirect-btn"
                  onClick={() => {
                    redirectTo();
                    sendSimpleUiEvent(manageOptionsButtonClickTrackEvent);
                  }}
                  type="secondary"
                  size="medium"
                  id="consent-manage=options-button"
                >
                  {manageOptionsButton}
                </Button>
              )}
              {isSupportDeclineAllButton() && (
                <Button
                  onClick={() => {
                    declineAllClick();
                    sendSimpleUiEvent(declineAllButtonClickTrackEvent);
                  }}
                  type="secondary"
                  loading={declineAllLoading}
                  disabled={declineAllLoading}
                  size="medium"
                  id="consent-decline-all-button"
                >
                  {declineAllButton}
                </Button>
              )}
              <Button
                onClick={() => {
                  handleClick();
                  sendSimpleUiEvent(acceptAllButtonClickTrackEvent);
                }}
                disabled={loading}
                size="medium"
                type={isWorkFromHomePrinter ? 'primary' : 'secondary'}
                id="consent-accept-all-button"
              >
                {loading ? <ButtonLoaderWrapper><Loader type="circular" variant="small" /></ButtonLoaderWrapper> : acceptAllButton}
              </Button>
            </ReTargetUserConsentsButtonLargeContainer>
          );
        }
         return (
            <ReTargetUserConsentsButtonContainer
              data-testid="redirect-container"
              isDesktop={isDesktop}
              isRestOfWorld={isRestOfWorld}
              isRTL={isRTL}
              id="re-target-button-container"
              ref={userButtonContainerRef}
            >
              <Button
                onClick={() => {
                  handleClick();
                  sendSimpleUiEvent(acceptAllButtonClickTrackEvent);
                }}
                disabled={loading}
                size="medium"
                type={isWorkFromHomePrinter ? 'primary' : 'secondary'}
                id="consent-accept-all-button"
              >
                {loading ? <ButtonLoaderWrapper><Loader type="circular" variant="small" /></ButtonLoaderWrapper> : acceptAllButton}
              </Button>
              {isSupportDeclineAllButton() && (
                <Button
                  onClick={() => {
                    declineAllClick();
                    sendSimpleUiEvent(declineAllButtonClickTrackEvent);
                  }}
                  type="secondary"
                  disabled={declineAllLoading}
                  size="medium"
                  id="consent-decline-all-button"
                >
                  {declineAllLoading ? <ButtonLoaderWrapper><Loader type="circular" variant="small" /></ButtonLoaderWrapper> : declineAllButton}
                </Button>
              )}
              {!isWorkFromHomePrinter && !isCompanyTenantPrinter && !hasAppNameWJA && (
                <Button
                  data-testid="redirect-btn"
                  onClick={() => {
                    redirectTo();
                    sendSimpleUiEvent(manageOptionsButtonClickTrackEvent);
                  }}
                  type="secondary"
                  size="medium"
                  id="consent-manage=options-button"
                >
                  {manageOptionsButton}
                </Button>
              )}
            </ReTargetUserConsentsButtonContainer>
          );
      }

      if (isPrinterLargeScreen) {
        return (
          <ReTargetButtonContainer
            data-testid="redirect-container"
            isDesktop={isDesktop}
            isRestOfWorld={isRestOfWorld}
            isRTL={isRTL}
            id="re-target-button-container"
          >
            <ReTargetPrinterButtonLargeContainer>
              {!isWorkFromHomePrinter && !isCompanyTenantPrinter && !hasAppNameWJA && (
                <Button
                  data-testid="redirect-btn"
                  onClick={() => {
                    redirectTo();
                    sendSimpleUiEvent(manageOptionsButtonClickTrackEvent);
                  }}
                  type="secondary"
                  size="medium"
                  id="consent-manage=options-button"
                >
                  {manageOptionsButton}
                </Button>
              )}
              {isSupportDeclineAllButton() && (
                <Button
                  onClick={() => {
                    declineAllClick();
                    sendSimpleUiEvent(declineAllButtonClickTrackEvent);
                  }}
                  type="secondary"
                  disabled={declineAllLoading}
                  size="medium"
                  id="consent-decline-all-button"
                >
                  {declineAllLoading ? <ButtonLoaderWrapper><Loader type="circular" variant="small" /></ButtonLoaderWrapper> : declineAllButton}
                </Button>
              )}
              <Button
                onClick={() => {
                  handleClick();
                  sendSimpleUiEvent(acceptAllButtonClickTrackEvent);
                }}
                disabled={loading}
                size="medium"
                type={isWorkFromHomePrinter ? 'primary' : 'secondary'}
                id="consent-accept-all-button"
              >
                {loading ? <ButtonLoaderWrapper><Loader type="circular" variant="small" /></ButtonLoaderWrapper> : acceptAllButton}
              </Button>
            </ReTargetPrinterButtonLargeContainer>
          </ReTargetButtonContainer>
        );
      }
      return (
        <ReTargetButtonContainer
          data-testid="redirect-container"
          isDesktop={isDesktop}
          isRestOfWorld={isRestOfWorld}
          isRTL={isRTL}
          id="re-target-button-container"
        >
          <Button
            onClick={() => {
              handleClick();
              sendSimpleUiEvent(acceptAllButtonClickTrackEvent);
            }}
            disabled={loading}
            size="medium"
            type={isWorkFromHomePrinter ? 'primary' : 'secondary'}
            id="consent-accept-all-button"
          >
            {loading ? <ButtonLoaderWrapper><Loader type="circular" variant="small" /></ButtonLoaderWrapper> : acceptAllButton}
          </Button>
          {isSupportDeclineAllButton() && (
            <Button
              onClick={() => {
                declineAllClick();
                sendSimpleUiEvent(declineAllButtonClickTrackEvent);
              }}
              type="secondary"
              disabled={declineAllLoading}
              size="medium"
              id="consent-decline-all-button"
            >
              {declineAllLoading ? <ButtonLoaderWrapper><Loader type="circular" variant="small" /></ButtonLoaderWrapper> : declineAllButton}
            </Button>
          )}
          {!isWorkFromHomePrinter && !isCompanyTenantPrinter && !hasAppNameWJA && (
            <Button
              data-testid="redirect-btn"
              onClick={() => {
                redirectTo();
                sendSimpleUiEvent(manageOptionsButtonClickTrackEvent);
              }}
              type="secondary"
              size="medium"
              id="consent-manage=options-button"
            >
              {manageOptionsButton}
            </Button>
          )}
        </ReTargetButtonContainer>
      );
    }

    return (
      <StyledButton
        onClick={handleClick}
        disabled={loading}
        small={smallButton}
        id="consent-button"
      >
        {loading ? <ButtonLoaderWrapper><Loader type="circular" variant="small" /></ButtonLoaderWrapper> : (hasAppNameSureSupply ? continueButtonText : continueButton)}
      </StyledButton>
    );
  };

  const getWelcomeContainer = () => {
    if (printerConsents || userConsents) {
      {
        if (userConsents && !isWorkFromHomePrinter) {
          return(              
                <RebrandEmptyPageWrapper>
                  <EmptyPage>
                    {body && alpacaData !== null && title && (
                      <RebrandEmptyPageContentWrapper>
                        <RebrandUserConsentsTitle  isRTL={isRTL} id="title-text" isDesktop={isDesktop}>
                          {title}
                        </RebrandUserConsentsTitle>
                        <EmptyCard
                          id="settings-container"
                          data-testid="privacy-settings"
                          className="privacy-settings-card"
                        >
                          <RebrandUserConsentsCardContent isRTL={isRTL}>
                            <ReTargetContentContainer
                              id="content-container"
                              isDeclineAllButtonAppeared={
                                isRestOfWorld && !isWorkFromHomePrinter && !isCompanyTenantPrinter
                              }
                              isCompanyTenantPrinter={isCompanyTenantPrinter || hasAppNameWJA}
                            >
                              {body && alpacaData !== null && (
                                <ReTargetUserConsentsBody isRTL={isRTL} id="hpsmart-content" reTarget={reTarget}>
                                  {body && body.match(/<[a-z]+>/) ? (
                                    <div>
                                      <span dangerouslySetInnerHTML={{ __html: body }} />
                                      {!isRestOfWorld ? (
                                        <BodyList
                                          dangerouslySetInnerHTML={{ __html: secondaryBodyText }}
                                          isRTL={isRTL}
                                        />
                                      ) : (
                                        <BodyList
                                          dangerouslySetInnerHTML={{ __html: supplementalText }}
                                          isRTL={isRTL}
                                        />
                                      )}
                                    </div>
                                  ) : (
                                    <div>{body}</div>
                                  )}
                                </ReTargetUserConsentsBody>
                              )}
                              {errorText && errorCode === 400 && (
                                <ReTargetUserConsentsBody id="hpsmart-content" reTarget={reTarget}>
                                  {errorText && errorText.match(/<[a-z]+>/) ? (
                                    <div>
                                      <span dangerouslySetInnerHTML={{ __html: errorText }} />
                                    </div>
                                  ) : (
                                    <div style={{ textTransform: 'capitalize', fontSize: '2.975rem' }}>
                                      {errorText}
                                    </div>
                                  )}
                                </ReTargetUserConsentsBody>
                              )}
                              {errorMessage && errorCode === 500 && (
                                <ReTargetUserConsentsBody id="hpsmart-content" reTarget={reTarget}>
                                  {errorMessage && errorMessage.match(/<[a-z]+>/) ? (
                                    <div>
                                      <span dangerouslySetInnerHTML={{ __html: errorMessage }} />
                                    </div>
                                  ) : (
                                    <div style={{ textTransform: 'capitalize', fontSize: '2.975rem' }}>
                                      {errorMessage}
                                    </div>
                                  )}
                                </ReTargetUserConsentsBody>
                              )}
                            </ReTargetContentContainer>
                          </RebrandUserConsentsCardContent>
                        </EmptyCard>
                  {alpacaData !== null && userConsents && getButtonContainer()}
                </RebrandEmptyPageContentWrapper>
              )}
              </EmptyPage>
              </RebrandEmptyPageWrapper>
          )
        }

       
        return (
          <RebrandBackGround isRTL={isRTL}>
            <DeviceListPage>
              {body && alpacaData !== null && title && (
                <RebrandEmptyCard>
                  <EmptyCard
                    id="settings-container"
                    data-testid="privacy-settings"
                    className="privacy-settings-card"
                  >
                    <RebrandCardContent>
                      <ReTargetContentContainer
                        id="content-container"
                        isDeclineAllButtonAppeared={
                          isRestOfWorld && !isWorkFromHomePrinter && !isCompanyTenantPrinter
                        }
                        isCompanyTenantPrinter={isCompanyTenantPrinter || hasAppNameWJA}
                      >
                        {body && alpacaData !== null && (
                          <Title id="title-text" isDesktop={isDesktop} isRTL={isRTL}>
                            {title}
                          </Title>
                        )}
                        {body && alpacaData !== null && (
                          <ReTargetBody id="hpsmart-content" reTarget={reTarget} isRTL={isRTL}>
                            {body && body.match(/<[a-z]+>/) ? (
                              <div>
                                <span dangerouslySetInnerHTML={{ __html: body }} />
                                {!isRestOfWorld ? (
                                  <BodyList
                                    dangerouslySetInnerHTML={{ __html: secondaryBodyText }}
                                    isRTL={isRTL}
                                  />
                                ) : (
                                  <BodyList
                                    dangerouslySetInnerHTML={{ __html: supplementalText }}
                                    isRTL={isRTL}
                                  />
                                )}
                              </div>
                            ) : (
                              <div>{body}</div>
                            )}
                          </ReTargetBody>
                        )}
                        {errorText && errorCode === 400 && (
                          <ReTargetBody id="hpsmart-content" reTarget={reTarget}>
                            {errorText && errorText.match(/<[a-z]+>/) ? (
                              <div>
                                <span dangerouslySetInnerHTML={{ __html: errorText }} />
                              </div>
                            ) : (
                              <div style={{ textTransform: 'capitalize', fontSize: '2.975rem' }}>
                                {errorText}
                              </div>
                            )}
                          </ReTargetBody>
                        )}
                        {errorMessage && errorCode === 500 && (
                          <ReTargetBody id="hpsmart-content" reTarget={reTarget}>
                            {errorMessage && errorMessage.match(/<[a-z]+>/) ? (
                              <div>
                                <span dangerouslySetInnerHTML={{ __html: errorMessage }} />
                              </div>
                            ) : (
                              <div style={{ textTransform: 'capitalize', fontSize: '2.975rem' }}>
                                {errorMessage}
                              </div>
                            )}
                          </ReTargetBody>
                        )}
                      </ReTargetContentContainer>
                      {alpacaData !== null && printerConsents && getButtonContainer()}
                    </RebrandCardContent>
                    {alpacaData !== null && userConsents && getButtonContainer()}
                  </EmptyCard>
                </RebrandEmptyCard>
              )}
            </DeviceListPage>
          </RebrandBackGround>
        );
      }
    }

    if (reTarget) {
      return (
        <ReTargetContainer
          id="welcome-container"
          data-testid="welcome-container-retarget"
          hasBody={Boolean(body)}
          reTarget={reTarget}
          isDesktop={isDesktop}
          isRTL={isRTL}
        >
          <ReTargetContentContainer
            id="content-container"
            isDeclineAllButtonAppeared={
              isRestOfWorld && !isWorkFromHomePrinter && !isCompanyTenantPrinter
            }
            isCompanyTenantPrinter={isCompanyTenantPrinter || hasAppNameWJA}
          >
            {!isCompanyTenantPrinter && !printerConsents && (
              <Graphic
                id="graphic"
                src={(() => {
                  if (hasAppNameEasyStart) {
                    return svg;
                  } else if (hasAppNameEasyStartPro) {
                    return easyStartproIcon;
                  } else if (hasAppNameWJA) {
                    return printerImage;
                  } else if (isWorkFromHomePrinter) {
                    return printerImage;
                  } else {
                    return svg;
                  }
                })()}
                alt="Printer and tablet line art"
                isWJA={hasAppNameWJA}
              />
            )}
            {body && alpacaData !== null && (
              <Title id="title-text" isDesktop={isDesktop}>
                {title}
              </Title>
            )}
            {body && alpacaData !== null && (
              <ReTargetBody id="hpsmart-content" reTarget={reTarget}>
                {body && body.match(/<[a-z]+>/) ? (
                  <div>
                    <span dangerouslySetInnerHTML={{ __html: body }} />
                    {!isRestOfWorld ? (
                      <BodyList
                        dangerouslySetInnerHTML={{ __html: secondaryBodyText }}
                        isRTL={isRTL}
                      />
                    ) : (
                      <BodyList
                        dangerouslySetInnerHTML={{ __html: supplementalText }}
                        isRTL={isRTL}
                      />
                    )}
                  </div>
                ) : (
                  <div>{body}</div>
                )}
              </ReTargetBody>
            )}
            {errorText && errorCode === 400 && (
              <ReTargetBody id="hpsmart-content" reTarget={reTarget}>
                {errorText && errorText.match(/<[a-z]+>/) ? (
                  <div>
                    <span dangerouslySetInnerHTML={{ __html: errorText }} />
                  </div>
                ) : (
                  <div style={{ textTransform: 'capitalize', fontSize: '2.975rem' }}>
                    {errorText}
                  </div>
                )}
              </ReTargetBody>
            )}
            {errorMessage && errorCode === 500 && (
              <ReTargetBody id="hpsmart-content" reTarget={reTarget}>
                {errorMessage && errorMessage.match(/<[a-z]+>/) ? (
                  <div>
                    <span dangerouslySetInnerHTML={{ __html: errorMessage }} />
                  </div>
                ) : (
                  <div style={{ textTransform: 'capitalize', fontSize: '2.975rem' }}>
                    {errorMessage}
                  </div>
                )}
              </ReTargetBody>
            )}
          </ReTargetContentContainer>
          {alpacaData !== null && getButtonContainer()}
        </ReTargetContainer>
      );
    }

    if (hasAppNameSureSupply) {
      return (
        <SureSupplyContainer id="suresupply-main-container">
          <LogoContainer id="logo-container">
            <LogoHp appearance="multicolor" size={100} />
          </LogoContainer>
          <BodySureSupply id="suresupply-content">
            <div dangerouslySetInnerHTML={{ __html: sureSupplyAnalyticsContent }} />
          </BodySureSupply>
          <ToggleLayout>
            <ToggleLabelHeader>{buttonTitle}</ToggleLabelHeader>
            <Toggle
              id="basic-suresupply-toggle"
              data-testid="setsuresupply-toggle"
              on={sureSupplyAnalyticsConsentToggle}
              onChange={(e) => {
                setSureSupplyAnalyticsConsentToggle(e);
              }}
            />
          </ToggleLayout>
          {getButtonContainer()}
        </SureSupplyContainer>
      );
    }

    return (
      <Container
        data-testid="welcome-container"
        id="welcome-container"
        hasBody={Boolean(body)}
        reTarget={reTarget}
        isRTL={isRTL}
      >
        <Graphic id="graphic" src={svg} alt="Printer and tablet line art" />
        <Title id="title-text">{title}</Title>
        {!reTarget && (
          <Body id="external-content">
            {alpacaData && alpacaData.match(/<[a-z]+>/) ? (
              <span dangerouslySetInnerHTML={{ __html: alpacaData }} />
            ) : (
              (alpacaData,
              (
                <div
                  id="welcome-text"
                  dangerouslySetInnerHTML={{ __html: requiredWelcomeContent }}
                />
              ))
            )}
          </Body>
        )}
        {body && (
          <Body id="hpsmart-content" reTarget={reTarget}>
            {body && body.match(/<[a-z]+>/) ? (
              <span dangerouslySetInnerHTML={{ __html: body }} />
            ) : (
              <div>{body}</div>
            )}
          </Body>
        )}
        {getButtonContainer()}
      </Container>
    );
  };

  let requiredPrinterConsentState;

  const checkRequiredConsentState = (data) => {
    const consentValues = Object.values(data);
    consentValues.map((consentData) => {
      if (consentData.purposeId === requiredBasicPrinter) {
        requiredPrinterConsentState = consentData.state;
      }
    });
    return requiredPrinterConsentState;
  };

  if (alpacaData !== null) {
    checkRequiredConsentState(alpacaData);
  }

  if (!checkBasicFuncPrinter) {
    if (query.checkBasicFuncPrinter && query.redirectUrl) {
      window.location.replace(query.redirectUrl);
    } else {
      return getWelcomeContainer();
    }
  } else {
    if (requiredPrinterConsentState === 'OptedIn' && query.redirectUrl) {
      window.location.replace(query.redirectUrl);
    } else {
      return getWelcomeContainer();
    }
  }
};

export default SmartWelcomeRebrand;
